/**
 * `/dynamics/reports` — the user-report queue as its own screen (L2).
 *
 * The people who pressed "this app is slow right now" in the extension popup,
 * newest first, filterable by triage state, with one report open at a time.
 * It was a block at the bottom of `/dynamics/investigate` until 2026-08-21, and
 * that was the wrong shape twice over: it is where an investigation *starts*, so
 * it sat below the thing it feeds; and it is a work queue with its own window,
 * its own status filter and its own selection, which is a screen, not a panel.
 *
 * ## What is in the URL
 *
 * Everything that changes what you see: `?window=` (the store's own
 * `24h|7d|30d|90d`, not the console's KQL lookback), `?status=`
 * (`new|triaged|closed`, absent = every state) and `?report=` (the open report).
 * So a triage handover is a link. The **reason** is not in the URL — it is
 * session state the backend audit-logs, and putting it in a link would make it
 * someone else's reason.
 *
 * ## Two gates, and the honest states
 *
 * *Listing* the queue needs no reason: a report is its submitter's own data,
 * offered on purpose, and an investigator has to see what arrived before they
 * can say why they are looking. *Opening* one names a person, their comment and
 * their browser, so it is a per-user view — reason-gated and audit-logged like
 * everything else in the console, and the drawer refuses to fetch without one.
 *
 * Both are also behind the investigator allow-list (`NOVA_FO_INVESTIGATORS`),
 * so this page renders for anyone and then says, in plain words, that the queue
 * was not read. That is why the sidebar disables rather than hides the row: an
 * unreadable queue is information, an absent one implies there is no queue.
 *
 * The three states never collapse into each other — a 403 is not an empty
 * queue, an unreadable store is not an empty queue, and an empty queue means
 * nobody pressed the button. `reports-section.tsx` owns those words.
 */

import { Icon, KeyRound } from "@norican/icons";
import {
  Alert,
  type BreadcrumbItem,
  Breadcrumbs,
  Button,
  Card,
  PageHeader,
  Typography,
} from "@norican/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { NAV_SECTIONS } from "@/components/nav-sections";
import { useDrawerParam } from "@/lib/drill";

import { ACTIVITY_PARAM, DYNAMICS_PATH, DYNAMICS_REPORTS_PATH } from "./params";
import { ReasonControl, ReasonDialog, useInvestigationReason } from "./reason-gate";
import { RecordDrawer } from "./record-drawer";
import { ReportsSection } from "./reports-section";
import { ConsoleToasts } from "./toast-stack";

const SECTION = NAV_SECTIONS.find((section) => section.path === DYNAMICS_REPORTS_PATH);

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Reports", current: true },
];

export function DynamicsReportsPage() {
  return (
    <ConsoleToasts>
      <ReportsPageBody />
    </ConsoleToasts>
  );
}

function ReportsPageBody() {
  const gate = useInvestigationReason();
  // Unlike the investigate page, the dialog does not open itself: the queue
  // lists without a reason, so demanding one on arrival would gate a view that
  // is not gated. It opens when the reader tries to look at a person.
  const [dialogOpen, setDialogOpen] = useState(false);
  const drawer = useDrawerParam(ACTIVITY_PARAM);

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Reports from users"
        subtitle={
          SECTION?.description ??
          'The queue of people who pressed "this app is slow right now" in the extension popup.'
        }
        actions={<ReasonControl reason={gate.reason} onChange={() => setDialogOpen(true)} />}
      />

      <ReasonDialog
        key={gate.reason ?? "none"}
        open={dialogOpen}
        initialReason={gate.reason}
        onCancel={() => setDialogOpen(false)}
        onSubmit={(reason) => {
          if (gate.setReason(reason)) {
            setDialogOpen(false);
          }
        }}
      />

      {gate.reason == null && (
        <Card variant="outline" accent="left" accentTone="warning">
          <Card.Body>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Icon icon={KeyRound} size="md" aria-hidden="true" />
                <div className="flex flex-col gap-1">
                  <Typography variant="heading-4" as="h2">
                    The queue lists without a reason. Opening a report needs one.
                  </Typography>
                  <Typography variant="body-sm" tone="muted">
                    A report names the person who sent it, so reading one is a per-user view: the
                    call carries a ticket or report id (or short free text), and the backend logs
                    your name against theirs. Kept for this browser session only (D7).
                  </Typography>
                </div>
              </div>
              <Button onClick={() => setDialogOpen(true)}>Enter a reason</Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <ReportsSection reason={gate.reason} onOpenRecord={(activityId) => drawer.open(activityId)} />

      {/* A report's matched F&O load opens the §5 record here rather than
          bouncing through the investigate page: the reader is already looking at
          one person for one stated reason, and a route change would lose the
          queue position they came from. */}
      <RecordDrawer
        activityId={drawer.value}
        reason={gate.reason}
        onClose={drawer.close}
        breadcrumbs={[
          { label: "Dynamics", to: DYNAMICS_PATH },
          { label: "Reports", to: DYNAMICS_REPORTS_PATH },
        ]}
      />

      <Alert tone="neutral" title="What a report is, and what it is not">
        The click is the consent that lets NOVA keep a report with its sender&apos;s mail — it is
        the one identity-bearing thing this product stores. It is not a measurement: a report with
        no matched form load means the person felt something the workspace does not see as a slow
        load, which is a finding about the telemetry, not a mistake by them.
      </Alert>
    </main>
  );
}
